import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, ArrowUpRight, Globe, ChevronDown } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";

const WORK_WITH_ME_EN = [
  { path: "/sprint", label: "Brand Reality Check Audit" },
  { path: "/brand-foundation", label: "Brand Foundation" },
  { path: "/visual-identity", label: "Full Brand System" },
  { path: "/consultation", label: "1:1 Consultation" },
];

const WORK_WITH_ME_PL = [
  { path: "/pl/sprint", label: "Brand Reality Check Audit" },
  { path: "/pl/brand-foundation", label: "Brand Foundation" },
  { path: "/pl/visual-identity", label: "Full Brand System" },
  { path: "/pl/consultation", label: "Konsultacja 1:1" },
];

const NAV_LINKS_EN = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { id: "work", label: "Work with me", hasDropdown: true },
  { path: "/ai-products", label: "AI Products" },
];

const NAV_LINKS_PL = [
  { path: "/pl", label: "Strona główna" },
  { path: "/pl/about", label: "O mnie" },
  { id: "work", label: "Współpraca", hasDropdown: true },
  { path: "/pl/ai-products", label: "Produkty AI" },
];

/* Map current path to its other-language equivalent */
const PL_ROUTES = {
  "/": "/pl",
  "/about": "/pl/about",
  "/sprint": "/pl/sprint",
  "/brand-foundation": "/pl/brand-foundation",
  "/visual-identity": "/pl/visual-identity",
  "/consultation": "/pl/consultation",
  "/ai-products": "/pl/ai-products",
  "/services": "/pl/services",
  "/contact": "/pl/contact",
};
const EN_ROUTES = Object.fromEntries(Object.entries(PL_ROUTES).map(([en, pl]) => [pl, en]));

function getOtherLangPath(currentPath, targetLang) {
  if (targetLang === "PL") {
    return PL_ROUTES[currentPath] || "/pl";
  }
  return EN_ROUTES[currentPath] || currentPath.replace(/^\/pl/, "") || "/";
}

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const isPL = location.pathname.startsWith("/pl");
  const lang = isPL ? "PL" : "EN";

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const workRef = useRef(null);

  const NAV_LINKS = isPL ? NAV_LINKS_PL : NAV_LINKS_EN;
  const WORK_WITH_ME = isPL ? WORK_WITH_ME_PL : WORK_WITH_ME_EN;
  const ctaLabel = isPL ? "Umów się" : "Get in Touch";

  const switchLang = () => {
    const targetLang = isPL ? "EN" : "PL";
    const newPath = getOtherLangPath(location.pathname, targetLang);
    setMenuOpen(false);
    navigate(newPath);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (workRef.current && !workRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isWorkPage = WORK_WITH_ME.some(s => location.pathname === s.path);

  const toggleDropdown = (id) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const toggleMobileExpanded = (id) => {
    setMobileExpanded(mobileExpanded === id ? null : id);
  };

  const renderDropdownItems = (items) => (
    items.map((item) => (
      <Link
        key={item.path}
        to={item.path}
        className={`block px-5 py-2.5 font-body text-xs tracking-[1px] uppercase transition-colors duration-200 ${location.pathname === item.path ? "text-white" : "text-white/50 hover:text-white/80"}`}
      >
        {item.label}
      </Link>
    ))
  );

  const renderMobileItems = (items) => (
    items.map((item) => (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setMenuOpen(false)}
        className={`font-body text-lg transition-colors duration-300 ${location.pathname === item.path ? "text-[#BE123C]" : "text-white/40 hover:text-white/70"}`}
      >
        {item.label}
      </Link>
    ))
  );

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#1C1210]/95 backdrop-blur-md py-3" : "bg-transparent py-5"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between">
          <Link to={isPL ? "/pl" : "/"} className="flex items-center">
            <img src="/images/logo-white.png" alt="KALZA" className="h-8 md:h-9 w-auto" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              link.hasDropdown ? (
                <div key={link.id} className="relative" ref={workRef}>
                  <button
                    onClick={() => toggleDropdown(link.id)}
                    className={`font-body text-xs tracking-[1.5px] uppercase transition-colors duration-300 flex items-center gap-1 ${
                      isWorkPage ? "text-white" : "text-white/50 hover:text-white/80"
                    }`}
                  >
                    {link.label}
                    <ChevronDown size={12} className={`transition-transform duration-200 ${openDropdown === link.id ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === link.id && (
                    <div className="absolute top-full left-0 mt-3 bg-[#1C1210]/95 backdrop-blur-md border border-white/10 rounded-lg py-2 min-w-[260px]">
                      {renderDropdownItems(WORK_WITH_ME)}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-body text-xs tracking-[1.5px] uppercase transition-colors duration-300 ${location.pathname === link.path ? "text-white" : "text-white/50 hover:text-white/80"}`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <button
              onClick={switchLang}
              className="flex items-center gap-1.5 text-white/40 hover:text-white/70 transition-colors duration-300 font-body text-xs tracking-[1px]"
            >
              <Globe size={14} />
              {lang}
            </button>
            <a
              href={CAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-xs tracking-[1.5px] uppercase bg-[#BE123C] text-white px-5 py-2.5 rounded-full hover:bg-[#9F1239] transition-colors duration-300"
            >
              {ctaLabel}
            </a>
            <button
              onClick={() => setMenuOpen(true)}
              className="text-white/50 hover:text-white transition-colors duration-300 ml-1"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>

          <div className="md:hidden flex items-center gap-4">
            <button
              onClick={switchLang}
              className="flex items-center gap-1 text-white/40 hover:text-white/70 transition-colors duration-300 font-body text-xs"
            >
              <Globe size={14} />{lang}
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              className="text-white/50 hover:text-white transition-colors duration-300"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      <div className={`fixed inset-0 z-[60] bg-[#1C1210] transition-all duration-500 ${menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
          <Link to={isPL ? "/pl" : "/"} className="flex items-center" onClick={() => setMenuOpen(false)}>
            <img src="/images/logo-white.png" alt="KALZA" className="h-8 md:h-9 w-auto" />
          </Link>
          <button
            onClick={() => setMenuOpen(false)}
            className="text-white/50 hover:text-white transition-colors duration-300"
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[70vh] gap-6">
          {NAV_LINKS.map((link, i) => (
            link.hasDropdown ? (
              <div key={link.id} className="flex flex-col items-center">
                <button
                  onClick={() => toggleMobileExpanded(link.id)}
                  className={`font-serif text-3xl md:text-4xl transition-all duration-300 flex items-center gap-2 ${
                    isWorkPage ? "text-[#BE123C]" : "text-white/60 hover:text-white"
                  }`}
                  style={{
                    transitionDelay: menuOpen ? `${i * 60}ms` : "0ms",
                    transform: menuOpen ? "translateY(0)" : "translateY(20px)",
                    opacity: menuOpen ? 1 : 0,
                  }}
                >
                  {link.label}
                  <ChevronDown size={20} className={`transition-transform duration-200 ${mobileExpanded === link.id ? 'rotate-180' : ''}`} />
                </button>
                {mobileExpanded === link.id && (
                  <div className="flex flex-col items-center gap-3 mt-3">
                    {renderMobileItems(WORK_WITH_ME)}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className={`font-serif text-3xl md:text-4xl transition-all duration-300 ${location.pathname === link.path ? "text-[#BE123C]" : "text-white/60 hover:text-white"}`}
                style={{
                  transitionDelay: menuOpen ? `${i * 60}ms` : "0ms",
                  transform: menuOpen ? "translateY(0)" : "translateY(20px)",
                  opacity: menuOpen ? 1 : 0,
                }}
              >
                {link.label}
              </Link>
            )
          ))}
          <div
            className="flex items-center gap-4 mt-4"
            style={{
              transitionDelay: menuOpen ? `${NAV_LINKS.length * 60}ms` : "0ms",
              opacity: menuOpen ? 1 : 0,
            }}
          >
            <button onClick={() => { if (isPL) switchLang(); }} className={`font-body text-sm ${!isPL ? "text-white" : "text-white/40"}`}>EN</button>
            <span className="text-white/20">|</span>
            <button onClick={() => { if (!isPL) switchLang(); }} className={`font-body text-sm ${isPL ? "text-white" : "text-white/40"}`}>PL</button>
          </div>
          <a
            href={CAL_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 font-body text-xs tracking-[2px] uppercase bg-[#E11D48] text-white px-8 py-4 rounded-full hover:bg-[#BE123C] transition-colors duration-300 inline-flex items-center gap-2"
            style={{
              transitionDelay: menuOpen ? `${(NAV_LINKS.length + 1) * 60}ms` : "0ms",
              transform: menuOpen ? "translateY(0)" : "translateY(20px)",
              opacity: menuOpen ? 1 : 0,
            }}
          >
            {ctaLabel} <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </>
  );
}
